import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import type { DayEntry, WorkPeriod } from './day-entry';

@customElement('monthly-hours-list')
export class MonthlyHoursList extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Arial, sans-serif;
    }
    .header,
    .totals,
    .work-period {
      display: flex;
      padding: 10px;
    }
    .header {
      font-weight: bold;
      background: #f8f8f8;
      position: sticky;
      top: 0;
      border-bottom: 2px solid #ddd;
    }
    .day {
      border-bottom: 1px solid #ddd;
      padding: 10px;
    }
    .day-header {
      font-weight: bold;
      margin-bottom: 5px;
    }
    .work-hours {
      flex: 2;
      text-align: left;
    }
    .salaried-hours,
    .extra-hours,
    .pay-system-hours {
      flex: 1;
      text-align: right;
    }
    .icons {
      display: flex;
      gap: 8px;
      flex: 1;
      justify-content: center;
    }
    .totals {
      font-weight: bold;
      background: #e8e8e8;
      border-top: 2px solid #ddd;
    }
    button {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 1.2em;
    }
  `;
  @property({ type: Array }) days: DayEntry[] = [];

  render() {
    const totalSalariedHours = this.days.reduce(
      (sum, day) => sum + day.workPeriods.reduce((pSum, p) => pSum + p.salariedHours, 0),
      0,
    );
    const totalExtraHours = this.days.reduce(
      (sum, day) => sum + day.workPeriods.reduce((pSum, p) => pSum + p.extraHours, 0),
      0,
    );
    const totalPaySystemHours = this.days.reduce(
      (sum, day) => sum + day.workPeriods.reduce((pSum, p) => pSum + p.paySystemHours, 0),
      0,
    );

    return html`
      <div>
        <div class="header">
          <div class="work-hours"></div>
          <div class="salaried-hours">Normaltid</div>
          <div class="extra-hours">Overskudd</div>
          <div class="pay-system-hours">Tripletex</div>
          <div class="icons"></div>
        </div>
        ${map(
          this.days,
          (day: DayEntry) => html`
            <div class="day">
              <div class="day-header">${day.date}</div>
              ${day.workPeriods.length > 0
                ? html`
                    ${map(
                      day.workPeriods,
                      (period: WorkPeriod) => html`
                        <div class="work-period">
                          <div class="work-hours">${period.from} - ${period.to}</div>
                          <div class="salaried-hours">${period.salariedHours}t</div>
                          <div class="extra-hours">${period.extraHours}t</div>
                          <div class="pay-system-hours">${period.paySystemHours}t</div>
                          <div class="icons">
                            <button @click="${() => this.syncHours(day, period)}">🔄</button>
                            <button @click="${() => this.syncBothWays(day, period)}">🔁</button>
                            <button @click="${() => this.editHours(day, period)}">✏️</button>
                          </div>
                        </div>
                      `,
                    )}
                  `
                : html`<div>No work periods</div>`}
            </div>
          `,
        )}
        <div class="totals">
          <div class="work-hours">Total</div>
          <div class="salaried-hours">${totalSalariedHours}h</div>
          <div class="extra-hours">${totalExtraHours}h</div>
          <div class="pay-system-hours">${totalPaySystemHours}h</div>
          <div class="icons"></div>
        </div>
      </div>
    `;
  }

  private syncHours(day: DayEntry, period: WorkPeriod): void {
    console.log(`Syncing hours for ${day.date} (${period.from} - ${period.to})`);
  }

  private syncBothWays(day: DayEntry, period: WorkPeriod): void {
    console.log(`Syncing hours both ways for ${day.date} (${period.from} - ${period.to})`);
  }

  private editHours(day: DayEntry, period: WorkPeriod): void {
    console.log(`Editing hours for ${day.date} (${period.from} - ${period.to})`);
  }
}

// HTML Element Tag Map
declare global {
  interface HTMLElementTagNameMap {
    'monthly-hours-list': MonthlyHoursList;
  }
}
